import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SettingsIcon from '@mui/icons-material/Settings';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import {
  COLUMNA_ID_CLIENTE,
  COLUMNA_ID_PROVEEDOR,
  getCamposRequeridosImportSaldos,
  getColumnasImportSaldos,
  INSTRUCCIONES_IMPORT_SALDOS,
  type ModoImportacionSaldosCtaCte,
} from '../config/saldosCtaCteImportColumns';
import cuentaCorrienteService, {
  type ImportacionSaldoCtaCtePayload,
} from '../services/cuentaCorrienteService';
import SearchableSelect from './SearchableSelect';
import { useSucursalesVentaFiltro, sucursalVentaSelectOptions } from '../hooks/useSucursalesVentaFiltro';

interface ImportarSaldosCtaCteDialogProps {
  open: boolean;
  onClose: () => void;
  modo: ModoImportacionSaldosCtaCte;
  onImportado?: () => void;
}

type ImportResult = {
  total: number;
  exitosos: number;
  conErrores: number;
  mensajesError: string[];
};

type ValidPayloadRow = {
  payload: ImportacionSaldoCtaCtePayload;
  rowIndex: number;
};

type PreviewState = {
  validas: ValidPayloadRow[];
  errores: string[];
  totalFilas: number;
};

const BATCH_SIZE = 100;

const excelSerialToIsoDate = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) return null;
    const y = String(parsed.y).padStart(4, '0');
    const m = String(parsed.m).padStart(2, '0');
    const d = String(parsed.d).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const dmy = raw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  }
  const asDate = new Date(raw);
  if (Number.isNaN(asDate.getTime())) return null;
  return asDate.toISOString().slice(0, 10);
};

const parseNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  let raw = value;
  if (typeof raw === 'string') {
    raw = raw.replace(/[,]/g, '.').replace(/\s/g, '');
  }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const formatMoney = (n: number) =>
  n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const esCredito = (tipo: string) => {
  const t = tipo.trim().toUpperCase();
  return t === 'CREDITO' || t === 'CRÉDITO' || t === '80';
};

export const ImportarSaldosCtaCteDialog: React.FC<ImportarSaldosCtaCteDialogProps> = ({
  open,
  onClose,
  modo,
  onImportado,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales } =
    useSucursalesVentaFiltro({ resetPreferidaOnNavigation: true });

  const [importPreviewRows, setImportPreviewRows] = useState<Record<string, unknown>[]>([]);
  const [importFileName, setImportFileName] = useState('');
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [isProcessingImport, setIsProcessingImport] = useState(false);
  const [importProgress, setImportProgress] = useState<{ procesados: number; total: number } | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [sucursalAnchor, setSucursalAnchor] = useState<HTMLElement | null>(null);

  const columnas = getColumnasImportSaldos(modo);
  const idColumna = modo === 'clientes' ? COLUMNA_ID_CLIENTE : COLUMNA_ID_PROVEEDOR;
  const titulo = modo === 'clientes' ? 'Importar saldos CxC' : 'Importar saldos CxP';
  const plantillaNombre =
    modo === 'clientes' ? 'PlantillaSaldosClientes.xlsx' : 'PlantillaSaldosProveedores.xlsx';

  const totalesPreview = useMemo(() => {
    if (!preview) return null;
    const sumaSaldo = preview.validas.reduce((acc, r) => acc + r.payload.saldoActual, 0);
    let sumaCredito = 0;
    let sumaDebito = 0;
    const porMoneda = new Map<number, number>();
    for (const row of preview.validas) {
      if (esCredito(row.payload.tipo)) sumaCredito += row.payload.saldoActual;
      else sumaDebito += row.payload.saldoActual;
      porMoneda.set(
        row.payload.moneda,
        (porMoneda.get(row.payload.moneda) ?? 0) + row.payload.saldoActual
      );
    }
    return { sumaSaldo, sumaCredito, sumaDebito, porMoneda };
  }, [preview]);

  const resetState = () => {
    setImportPreviewRows([]);
    setImportFileName('');
    setPreview(null);
    setImportResult(null);
    setImportProgress(null);
    setIsProcessingImport(false);
    setSucursalAnchor(null);
  };

  const handleClose = () => {
    if (isProcessingImport) return;
    resetState();
    onClose();
  };

  const handleDescargarPlantilla = () => {
    try {
      const wb = XLSX.utils.book_new();
      const wsDatos = XLSX.utils.aoa_to_sheet([[...columnas]]);
      XLSX.utils.book_append_sheet(wb, wsDatos, modo === 'clientes' ? 'SaldosClientes' : 'SaldosProveedores');

      const instrucciones: (string | number)[][] = [
        ['Campo', 'Etiqueta', 'Descripción'],
        [
          idColumna,
          modo === 'clientes' ? 'Nro. cliente' : 'Nro. proveedor',
          'Identificador del tercero (obligatorio)',
        ],
        ...INSTRUCCIONES_IMPORT_SALDOS,
        [
          'nroSuc',
          'Sucursal',
          'Se elige en el diálogo de importación (no va en el Excel). Letra X + numerador SucVenta.',
        ],
      ];
      const wsInst = XLSX.utils.aoa_to_sheet(instrucciones);
      XLSX.utils.book_append_sheet(wb, wsInst, 'Instrucciones');
      XLSX.writeFile(wb, plantillaNombre);
    } catch {
      enqueueSnackbar('No se pudo generar la plantilla', { variant: 'error' });
    }
  };

  const validarFilas = (
    rows: Record<string, unknown>[],
    nroSuc: number
  ): PreviewState => {
    const camposRequeridos = getCamposRequeridosImportSaldos(modo);
    const errores: string[] = [];
    const validas: ValidPayloadRow[] = [];

    if (!rows.length) {
      return { validas, errores: ['El archivo no tiene filas de datos.'], totalFilas: 0 };
    }

    const headers = Object.keys(rows[0] ?? {});
    const headersLower = headers.map((h) => String(h).toLowerCase().trim());
    const noEncontrados = columnas.filter((h) => !headersLower.includes(h.toLowerCase()));
    if (noEncontrados.length > 0) {
      return {
        validas: [],
        errores: [`Columnas faltantes en el archivo: ${noEncontrados.join(', ')}`],
        totalFilas: rows.length,
      };
    }

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowNormalizado: Record<string, unknown> = {};
      Object.keys(row).forEach((key) => {
        rowNormalizado[key.toLowerCase().trim()] = row[key];
      });

      let valido = true;
      for (const campo of camposRequeridos) {
        const valor = rowNormalizado[campo.toLowerCase()];
        if (valor === undefined || valor === null || String(valor).trim() === '') {
          errores.push(`Fila ${i + 2}: El campo "${campo}" es obligatorio.`);
          valido = false;
          break;
        }
      }
      if (!valido) continue;

      const nroTercero = parseNumber(rowNormalizado[idColumna.toLowerCase()]);
      const nroDocumento = String(rowNormalizado.nrodocumento ?? '').trim();
      const saldoActual = parseNumber(rowNormalizado.saldoactual);
      const moneda = parseNumber(rowNormalizado.moneda);
      const fechaEmision = excelSerialToIsoDate(rowNormalizado.fechaemision);
      const fechaVencimiento = excelSerialToIsoDate(rowNormalizado.fechavencimiento);
      const tipo = String(rowNormalizado.tipo ?? '').trim();
      const codigoArticulo = String(rowNormalizado.codigoarticulo ?? '').trim();
      const descripcionRaw = String(rowNormalizado.descripcion ?? '').trim();

      if (nroTercero == null || nroTercero <= 0) {
        errores.push(`Fila ${i + 2}: "${idColumna}" debe ser un número mayor a 0.`);
        continue;
      }
      if (!nroDocumento) {
        errores.push(`Fila ${i + 2}: "nroDocumento" (comprobante original) es obligatorio.`);
        continue;
      }
      if (saldoActual == null || saldoActual < 0) {
        errores.push(`Fila ${i + 2}: "saldoActual" debe ser un número ≥ 0.`);
        continue;
      }
      if (moneda == null || moneda < 0 || !Number.isInteger(moneda)) {
        errores.push(`Fila ${i + 2}: "moneda" debe ser un código numérico entero ≥ 0.`);
        continue;
      }
      if (!fechaEmision) {
        errores.push(`Fila ${i + 2}: "fechaEmision" inválida.`);
        continue;
      }
      if (!fechaVencimiento) {
        errores.push(`Fila ${i + 2}: "fechaVencimiento" inválida.`);
        continue;
      }
      const tipoUpper = tipo.toUpperCase();
      if (!['CREDITO', 'CRÉDITO', 'DEBITO', 'DÉBITO', '80', '81'].includes(tipoUpper)) {
        errores.push(`Fila ${i + 2}: "tipo" debe ser CREDITO, DEBITO, 80 o 81.`);
        continue;
      }
      if (!codigoArticulo) {
        errores.push(`Fila ${i + 2}: "codigoArticulo" es obligatorio.`);
        continue;
      }

      validas.push({
        rowIndex: i + 2,
        payload: {
          nroTercero,
          tipo,
          nroDocumento,
          fechaEmision,
          fechaVencimiento,
          saldoActual,
          moneda,
          nroSuc,
          codigoArticulo,
          descripcion: descripcionRaw || null,
        },
      });
    }

    return { validas, errores, totalFilas: rows.length };
  };

  const onImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const fileBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(fileBuffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
      setImportPreviewRows(rows);
      setImportFileName(file.name);
      setImportResult(null);
      setPreview(null);
      if (!rows.length) {
        enqueueSnackbar('El archivo no tiene filas de datos', { variant: 'warning' });
      }
    } catch {
      setImportPreviewRows([]);
      setImportFileName('');
      setPreview(null);
      setImportResult(null);
      enqueueSnackbar('No se pudo leer el archivo Excel', { variant: 'error' });
    }
  };

  const handleValidarYPrevia = () => {
    if (!importPreviewRows.length) return;
    if (filtroNroSuc == null || filtroNroSuc <= 0) {
      enqueueSnackbar('Seleccioná la sucursal de venta para numerar los saldos internos (letra X).', {
        variant: 'warning',
      });
      return;
    }
    const result = validarFilas(importPreviewRows, filtroNroSuc);
    setPreview(result);
    setImportResult(null);
    if (result.validas.length === 0) {
      enqueueSnackbar('No hay filas válidas para importar', { variant: 'warning' });
    }
  };

  const handleProcesarImportacion = async () => {
    if (!preview?.validas.length) return;
    if (filtroNroSuc == null || filtroNroSuc <= 0) {
      enqueueSnackbar('Seleccioná la sucursal de venta.', { variant: 'warning' });
      return;
    }

    // Reaplicar nroSuc por si cambió después de la previa
    const payloads = preview.validas.map((v) => ({
      ...v,
      payload: { ...v.payload, nroSuc: filtroNroSuc },
    }));

    setIsProcessingImport(true);
    setImportProgress({ procesados: 0, total: payloads.length });

    let fallos = preview.errores.length;
    const mensajesError = [...preview.errores];
    let exitos = 0;
    const bulkFn =
      modo === 'clientes'
        ? cuentaCorrienteService.createSaldosClientesBulk
        : cuentaCorrienteService.createSaldosProveedoresBulk;

    for (let start = 0; start < payloads.length; start += BATCH_SIZE) {
      const chunk = payloads.slice(start, start + BATCH_SIZE);
      try {
        const res = await bulkFn(chunk.map((x) => x.payload));
        exitos += res.exitosos;
        fallos += res.fallidos;
        res.errores.forEach((e) => {
          const rowIndex = chunk[e.indice]?.rowIndex ?? start + e.indice + 2;
          mensajesError.push(
            `Fila ${rowIndex} (${idColumna} ${e.nroTercero}, doc ${e.nroDocumento}): ${e.mensaje}`
          );
        });
      } catch (err: unknown) {
        const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
        const msg =
          anyErr?.response?.data?.message || anyErr?.message || 'Error al procesar el lote';
        chunk.forEach((c) => {
          mensajesError.push(`Fila ${c.rowIndex}: ${msg}`);
          fallos++;
        });
      }
      setImportProgress({
        procesados: Math.min(start + chunk.length, payloads.length),
        total: payloads.length,
      });
    }

    setImportResult({
      total: preview.totalFilas,
      exitosos: exitos,
      conErrores: fallos,
      mensajesError,
    });
    setIsProcessingImport(false);

    if (exitos > 0) {
      enqueueSnackbar(`Se importaron ${exitos} saldos correctamente`, { variant: 'success' });
      onImportado?.();
      resetState();
      onClose();
    }
  };

  const inputId = `excel-upload-saldos-${modo}`;

  const sucursalLabel = useMemo(() => {
    if (filtroNroSuc == null) return null;
    const opt = sucursalVentaSelectOptions(sucursales).find((o) => o.value === filtroNroSuc);
    return opt?.label ?? String(filtroNroSuc).padStart(5, '0');
  }, [filtroNroSuc, sucursales]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30 }} />
          <Typography variant="h6" fontWeight={700}>
            {titulo}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
            <Typography component="span" fontWeight={700} fontSize={18}>
              1. Descargar Plantilla
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Descargá la plantilla Excel con el formato correcto para importar saldos (
            {modo === 'clientes' ? 'clientes' : 'proveedores'}).
          </Typography>
          <Button
            variant="contained"
            color="info"
            fullWidth
            sx={{ fontWeight: 700, py: 1.2 }}
            startIcon={<CloudDownloadIcon />}
            onClick={handleDescargarPlantilla}
          >
            Descargar Plantilla
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
              <CloudUploadIcon color="success" sx={{ fontSize: 30, mr: 1 }} />
              <Typography component="span" fontWeight={700} fontSize={18}>
                2. Importar Plantilla
              </Typography>
            </Box>
            <Tooltip title="Sucursal de numeración (letra X / FormuN)">
              <IconButton
                size="small"
                color="primary"
                aria-label="Configurar sucursal de numeración"
                onClick={(e) => setSucursalAnchor(e.currentTarget)}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Seleccioná el Excel. Columnas: tipo, nroDocumento (original), fechas, saldoActual, moneda,
            codigoArticulo y descripcion (opcional).
          </Typography>
          {sucursalLabel && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Numeración: letra X · sucursal {sucursalLabel}
            </Typography>
          )}
          <Popover
            open={Boolean(sucursalAnchor)}
            anchorEl={sucursalAnchor}
            onClose={() => setSucursalAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { p: 2, width: 360, maxWidth: '90vw' } } }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Sucursal de numeración
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Los saldos se graban con letra X y el próximo número de SucVenta (FormuN). El nroDocumento
              del Excel queda como comprobante original (detalle).
            </Typography>
            <SearchableSelect
              label="Sucursal de venta"
              allowEmpty={false}
              loading={loadingSucursales}
              size="small"
              value={filtroNroSuc}
              onChange={(v) => {
                setFiltroNroSuc(v ?? null);
                setPreview(null);
              }}
              options={sucursalVentaSelectOptions(sucursales)}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' },
              }}
            />
          </Popover>
          <label htmlFor={inputId}>
            <input
              id={inputId}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={onImportChange}
            />
            <Button
              variant="contained"
              fullWidth
              color="success"
              startIcon={<CloudUploadIcon />}
              component="span"
              sx={{ fontWeight: 700, py: 1.2 }}
            >
              Seleccionar Archivo
            </Button>
          </label>

          {importFileName && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight={600}>
                {importFileName} ({importPreviewRows.length} filas)
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                disabled={!importPreviewRows.length || isProcessingImport}
                onClick={handleValidarYPrevia}
                sx={{ fontWeight: 700 }}
              >
                Validar y ver previa
              </Button>
            </Box>
          )}

          {preview && totalesPreview && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Vista previa
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: '#e3f2fd',
                  border: '1px solid #bbdefb',
                  mb: 2,
                }}
              >
                <Typography variant="body2">
                  <strong>Filas:</strong> {preview.totalFilas} · <strong>Válidas:</strong>{' '}
                  {preview.validas.length} · <strong>Con error:</strong> {preview.errores.length}
                </Typography>
                <Typography variant="body2">
                  <strong>Total saldos válidos:</strong> {formatMoney(totalesPreview.sumaSaldo)}
                </Typography>
                <Typography variant="body2">
                  <strong>Créditos (80):</strong> {formatMoney(totalesPreview.sumaCredito)} ·{' '}
                  <strong>Débitos (81):</strong> {formatMoney(totalesPreview.sumaDebito)}
                </Typography>
                {totalesPreview.porMoneda.size > 1 && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Por moneda:</strong>{' '}
                    {Array.from(totalesPreview.porMoneda.entries())
                      .map(([m, s]) => `moneda ${m}: ${formatMoney(s)}`)
                      .join(' · ')}
                  </Typography>
                )}
                {filtroNroSuc != null && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Sucursal numeración:</strong> {filtroNroSuc} (letra X)
                  </Typography>
                )}
              </Box>

              {preview.validas.length > 0 && (
                <TableContainer sx={{ maxHeight: 240, mb: 2, border: '1px solid #eee', borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Fila</TableCell>
                        <TableCell>{idColumna}</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Original</TableCell>
                        <TableCell align="right">Saldo</TableCell>
                        <TableCell>Artículo</TableCell>
                        <TableCell>Descripción</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.validas.slice(0, 100).map((r) => (
                        <TableRow key={`${r.rowIndex}-${r.payload.nroDocumento}`}>
                          <TableCell>{r.rowIndex}</TableCell>
                          <TableCell>{r.payload.nroTercero}</TableCell>
                          <TableCell>{r.payload.tipo}</TableCell>
                          <TableCell>{r.payload.nroDocumento}</TableCell>
                          <TableCell align="right">{formatMoney(r.payload.saldoActual)}</TableCell>
                          <TableCell>{r.payload.codigoArticulo}</TableCell>
                          <TableCell>{r.payload.descripcion || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {preview.errores.length > 0 && (
                <Box
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #ffe0b2',
                    bgcolor: '#fff3e0',
                    p: 2,
                    maxHeight: 160,
                    overflow: 'auto',
                    mb: 2,
                  }}
                >
                  {preview.errores.map((msg, idx) => (
                    <Typography key={`${idx}-${msg}`} variant="body2" sx={{ mb: 0.5 }}>
                      {msg}
                    </Typography>
                  ))}
                </Box>
              )}

              <Button
                variant="contained"
                color="primary"
                disabled={!preview.validas.length || isProcessingImport || filtroNroSuc == null}
                onClick={handleProcesarImportacion}
                startIcon={
                  isProcessingImport ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />
                }
                sx={{ fontWeight: 700 }}
              >
                {isProcessingImport
                  ? importProgress
                    ? `Procesando ${importProgress.procesados} / ${importProgress.total}`
                    : 'Procesando...'
                  : `Importar ${preview.validas.length} saldos`}
              </Button>
            </Box>
          )}

          {importResult && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                Resultados de la Importación
              </Typography>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: importResult.conErrores > 0 ? '#ffebee' : '#e8f5e9',
                  border: '1px solid',
                  borderColor: importResult.conErrores > 0 ? '#ffcdd2' : '#c8e6c9',
                  mb: 2,
                }}
              >
                <Typography variant="body2">
                  <strong>Total procesados:</strong> {importResult.total}
                </Typography>
                <Typography variant="body2">
                  <strong>Exitosos:</strong> {importResult.exitosos}
                </Typography>
                <Typography variant="body2">
                  <strong>Con errores:</strong> {importResult.conErrores}
                </Typography>
              </Box>
              {importResult.mensajesError.length > 0 && (
                <Box
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #ffe0b2',
                    bgcolor: '#fff3e0',
                    p: 2,
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {importResult.mensajesError.map((msg, idx) => (
                    <Typography key={`${idx}-${msg}`} variant="body2" sx={{ mb: 0.5 }}>
                      {msg}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessingImport}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportarSaldosCtaCteDialog;
